import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  Target,
  DollarSign,
  Settings,
  RefreshCw,
  Zap,
  Eye,
  BarChart3,
} from "lucide-react";
import { Button, DatePicker, Input, message, Select } from "antd";
import { MMMScenarioPlannerProps } from "../../../../models/interfaces";
import dayjs, { Dayjs } from "dayjs";
import axiosInstance from "../../../../utils/axiosInstance";
import { getTenantId } from "../../../../utils/auth";

// API Types
interface ChannelConstraint {
  min_spend?: number;
  max_spend?: number;
}

interface Constraints {
  max_total_budget?: number;
  channel_constraints?: Record<string, ChannelConstraint>;
}

interface BudgetOptimizationRequest {
  tenantId: string;
  jobConfigId: string;
  optimization_goal: string;
  kpi_type: string;
  budget: number;
  start_date: any;
  end_date: any;
  time_horizon?: string; // optional since not in your JSON
  channels: string[];
  constraints?: Constraints;
}

interface KpiOptimizationRequest {
  kpi_type: string;
  budget: number;
  time_horizon: string;
  tenantId: string;
  jobConfigId: string;
  start_date: any;
  end_date: any;
  channels: string[];
  constraints?: {
    max_total_budget?: number;
    channel_constraints?: Record<string, ChannelConstraint>;
  };
}

interface ChannelAllocation {
  optimizedbudget: number;
  baseline: number;
  lift: number;
  predicted_kpi: number;
  predicted_channel_roi: number;
}

interface BudgetOptimizationResponse {
  scenario_id: string;
  optimization_id: string;
  status: string;
  results: {
    total_budget: number;
    optimized_allocation: Record<string, ChannelAllocation>;
    summary: {
      total_predicted_kpi: number;
      baseline_kpi: number;
      lift_value: number;
      lift_percent: number;
      average_roi: number;
    };
  };
  created_at: string;
}

interface KpiChannelAllocation {
  budget: number;
  predicted_conversions: number;
  roi: number;
}

interface KpiOptimizationResponse {
  data: {
    scenario_id: string;
    optimization_id: string;
    status: string;
    results: {
      target_kpi: {
        type: string;
        value: number;
      };
      estimated_budget: {
        total: number;
        confidence_interval: {
          lower: number;
          upper: number;
        };
      };
      optimized_allocation: Record<string, KpiChannelAllocation>;
      feasibility: {
        achievable: boolean;
        confidence: number;
        risk_factors: string[];
      };
    };
    model_metadata: {
      model_version: string;
      confidence_score: number;
      data_freshness: string;
      execution_time_ms: number;
    };
    created_at: string;
  };
}
const optimizeBudget = async (
  request: BudgetOptimizationRequest
): Promise<BudgetOptimizationResponse | undefined> => {
  try {
    const result = await axiosInstance.post(
      "/model/mmm/scenario-planner",
      request
    );
    return result?.data;
  } catch (error) {
    console.warn("API call failed, using mock response:", error);
  }
};

const optimizeKpi = async (
  request: KpiOptimizationRequest
): Promise<KpiOptimizationResponse | undefined> => {
  try {
    const result = await axiosInstance.post(
      "/model/mmm/scenario-planner",
      request
    );

    return result?.data;
  } catch (error) {
    console.warn("API call failed, using mock response:", error);
  }
};

export function buildConstraints(
  channelConstraints: Record<string, ChannelConstraint>
) {
  const constraints: Record<string, { min_spend: number; max_spend: number }> =
    {};

  Object.entries(channelConstraints).forEach(
    ([channel, constraint]: [any, any]) => {
      const { min, max } = constraint;
      if (min === 0 && max === 0) return; // skip zero values
      const channelKey = channel.toLowerCase().replace(/ /g, "_");
      constraints[channelKey] = {
        min_spend: min,
        max_spend: max,
      };
    }
  );

  return { constraints };
}

const MMMScenarioPlanner: React.FC<MMMScenarioPlannerProps> = ({
  variables,
  jobId,
}) => {
  const [activeTab, setActiveTab] = useState("budget");
  const [budget, setBudget] = useState("500000");
  const [kpiType, setKpiType] = useState("conversions");
  const [kpiGoal, setKpiGoal] = useState("");
  const [timeHorizon, setTimeHorizon] = useState("monthly");
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showConstraints, setShowConstraints] = useState(false);
  const [optimizationResponse, setOptimizationResponse] = useState<
    BudgetOptimizationResponse | KpiOptimizationResponse | null
  >(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const [channelConstraints, setChannelConstraints] = useState(
    variables.reduce((acc: any, key: any) => {
      acc[key] = { min: 0, max: 0, enabled: false };
      return acc;
    }, {} as Record<string, { min: number; max: number; enabled: boolean }>)
  );

  // Re-initialise constraints when the spend variables are loaded (they arrive
  // asynchronously from ViewResults via the parent's setSpendVariables).
  useEffect(() => {
    if (variables.length > 0) {
      setChannelConstraints(
        variables.reduce((acc: any, key: any) => {
          acc[key] = { min: 0, max: 0, enabled: false };
          return acc;
        }, {} as Record<string, { min: number; max: number; enabled: boolean }>)
      );
    }
  }, [variables]);

  const optimizedData = React.useMemo(() => {
    if (!optimizationResponse) {
      return [];
    }
    if (
      "results" in optimizationResponse &&
      "optimized_allocation" in optimizationResponse.results
    ) {
      if ("total_budget" in optimizationResponse.results) {
        const budgetResponse =
          optimizationResponse as BudgetOptimizationResponse;
        return Object.entries(budgetResponse.results.optimized_allocation).map(
          ([key, value]) => ({
            channel: key
              .split("_")
              .map(
                (word: string) => word.charAt(0).toUpperCase() + word.slice(1)
              )
              .join(" "),
            Original: value.baseline,
            optimized: value.optimizedbudget,
            roi: value.predicted_channel_roi,
            conversions: value.predicted_kpi,
          })
        );
      } else if ("target_kpi" in optimizationResponse.results) {
        const kpiResponse = optimizationResponse as any;
        return Object.entries(kpiResponse.results.optimized_allocation).map(
          ([key, value]: [any, any]) => ({
            channel: key
              .split("_")
              .map(
                (word: string) => word.charAt(0).toUpperCase() + word.slice(1)
              )
              .join(" "),
            Original: Math.round(value.budget * 0.85),
            optimized: value.budget,
            roi: value.roi,
            conversions: value.predicted_conversions,
          })
        );
      }
    }

    return [];
  }, [optimizationResponse]);

  const kpiOptions = [
    { value: "conversions", label: "Conversions", icon: "🎯" },
    { value: "sales", label: "Sales", icon: "💰" },
    { value: "leads", label: "Leads", icon: "📈" },
  ];

  const handleOptimize = async () => {
    if (!jobId) {
      message.error("No job selected.");
      return;
    }
    setIsOptimizing(true);
    setOptimizationResponse(null);
    try {
      const tenantId = getTenantId();
      // Use the actual spend variables from the model; fall back to constraint keys
      const channels = variables.length > 0
        ? variables
        : Object.keys(channelConstraints);

      const channelConstraintsPayload =
        Object.keys(channelConstraints).length > 0
          ? buildConstraints(channelConstraints).constraints
          : undefined;

      // Send dates as ISO strings so the backend can parse them reliably
      const startDateStr = selectedDate ? selectedDate.toISOString() : null;
      const endDateStr = endDate ? endDate.toISOString() : null;

      if (activeTab === "budget") {
        const request: BudgetOptimizationRequest = {
          budget: parseInt(budget),
          kpi_type: kpiType,
          time_horizon: timeHorizon,
          start_date: startDateStr,
          end_date: endDateStr,
          jobConfigId: jobId,
          tenantId,
          channels,
          constraints: {
            max_total_budget: parseInt(budget),
            channel_constraints: channelConstraintsPayload,
          },
          optimization_goal: "max_response",
        };
        const response = await optimizeBudget(request);
        if (!response) throw new Error("No response from optimizer");
        setOptimizationResponse(response);
      } else {
        if (!kpiGoal) {
          message.error("Please enter a target KPI value.");
          setIsOptimizing(false);
          return;
        }
        const request: KpiOptimizationRequest = {
          kpi_type: kpiType,
          tenantId,
          jobConfigId: jobId,
          end_date: endDateStr,
          start_date: startDateStr,
          budget: parseInt(kpiGoal),
          time_horizon: timeHorizon,
          channels,
          constraints: {
            max_total_budget: parseInt(budget) || 500000,
            channel_constraints: channelConstraintsPayload,
          },
        };
        const response = await optimizeKpi(request);
        if (!response) throw new Error("No response from optimizer");
        setOptimizationResponse(response);
      }

      setShowResults(true);
    } catch (error: any) {
      console.error("Optimization error:", error);
      message.error(
        error?.response?.data?.detail ||
        error?.message ||
        "Optimization failed. Please check inputs and try again."
      );
    } finally {
      setIsOptimizing(false);
    }
  };
  const estimatedBudget = kpiGoal ? Math.round(parseInt(kpiGoal) * 57.5) : 0;
  const totalOptimized = optimizedData.reduce(
    (sum, item) => sum + item.optimized,
    0
  );
  const totalBaseline = optimizedData.reduce(
    (sum, item) => sum + item.Original,
    0
  );
  const improvementPercent = React.useMemo(() => {
    if (optimizationResponse && "results" in optimizationResponse) {
      if (
        "summary" in optimizationResponse &&
        "improvement_percentage" in optimizationResponse
      ) {
        const budgetResponse =
          optimizationResponse as BudgetOptimizationResponse;
        return budgetResponse.results.summary.lift_percent.toFixed(1);
      }
    }
    return (((totalOptimized - totalBaseline) / totalBaseline) * 100).toFixed(
      1
    );
  }, [optimizationResponse, totalOptimized, totalBaseline]);

  const totalConversions = React.useMemo(() => {
    if (optimizationResponse && "results" in optimizationResponse) {
      if (
        "summary" in optimizationResponse.results &&
        "total_predicted_kpi" in optimizationResponse.results.summary
      ) {
        const budgetResponse =
          optimizationResponse as BudgetOptimizationResponse;
        return budgetResponse.results.summary.total_predicted_kpi;
      } else if ("target_kpi" in optimizationResponse.results) {
        const kpiResponse = optimizationResponse as any;
        return kpiResponse.results.target_kpi.value;
      }
    }
    return optimizedData.reduce((sum, item) => sum + item.conversions, 0);
  }, [optimizationResponse, optimizedData]);

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date);
  };
  const handleMinChange = (channel: string, value: string) => {
    setChannelConstraints((prev: any) => {
      const newValue = parseInt(value) || 0;

      // Calculate the total min excluding the current channel
      const currentTotal = Object.entries(prev).reduce(
        (sum, [key, val]: any) => (key === channel ? sum : sum + val.min),
        0
      );

      // ✅ Restrict based on 60% of budget
      if (currentTotal + newValue > Number(budget) * 0.6) {
        message.error(
          `Total Min value cannot exceed 60% of budget (${
            Number(budget) * 0.6
          })`
        );
        return prev;
      }

      return {
        ...prev,
        [channel]: {
          ...prev[channel],
          min: newValue,
        },
      };
    });
  };

  const handleMaxChange = (channel: string, value: string) => {
    setChannelConstraints((prev: any) => {
      const newValue = parseInt(value) || 0;
      const currentTotal = Object.entries(prev).reduce(
        (sum, [key, val]: any) => (key === channel ? sum : sum + val.max),
        0
      );

      if (currentTotal + newValue > Number(budget) * 1.5) {
        message.error(
          `Total Max value cannot exceed 150% of budget (${
            Number(budget) * 1.5
          })`
        );
        return prev;
      }

      return {
        ...prev,
        [channel]: {
          ...prev[channel],
          max: newValue,
        },
      };
    });
  };
  return (
    <div className="mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl">
            <BarChart3 className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              MMM Scenario Planner
            </h1>
            <p className="text-gray-600">
              AI-driven budget and KPI optimization for marketing mix modeling
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-8">
        <div className="">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Optimization Parameters
              </h2>
              <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
                <button
                  onClick={() => setActiveTab("budget")}
                  className={`flex-1 py-2 px-4  rounded-md text-sm font-medium transition-all ${
                    activeTab === "budget"
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <DollarSign className="h-4 w-4 inline mr-2" />
                  Optimize for Budget
                </button>
                <button
                  onClick={() => setActiveTab("kpi")}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                    activeTab === "kpi"
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <Target className="h-4 w-4 inline mr-2" />
                  Optimize for KPI
                </button>
              </div>
              {activeTab === "budget" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Total Budget ($)
                    </label>
                    <Input
                      type="number"
                      value={budget}
                      onChange={(e) => setBudget(e.target.value)}
                      placeholder="Enter total budget or leave empty for fallback"
                      className="w-full px-2 py-1 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      KPI Type
                    </label>
                    <Select
                      value={kpiType}
                      onChange={(e) => setKpiType(e)}
                      options={kpiOptions.map((ele) => ({
                        label: ele.label,
                        value: ele.value,
                      }))}
                      className="w-full border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date
                    </label>
                    <DatePicker
                      disabledDate={(current) => {
                        return current && current < dayjs().startOf("day");
                      }}
                      value={selectedDate}
                      onChange={handleDateChange}
                      format="MM/DD/YYYY"
                      className="w-full border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <DatePicker
                      disabledDate={(current) => {
                        if (!selectedDate) {
                          return current && current < dayjs().startOf("day");
                        }
                        return current && current < selectedDate.startOf("day");
                      }}
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(e);
                      }}
                      format="MM/DD/YYYY"
                      className="w-full border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
              {activeTab === "kpi" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      KPI Type
                    </label>
                    <Select
                      value={kpiType}
                      options={kpiOptions.map((ele) => ({
                        label: ele.label,
                        value: ele.value,
                      }))}
                      onChange={(e) => setKpiType(e)}
                      className="w-full border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target KPI
                    </label>
                    <Input
                      type="number"
                      value={kpiGoal}
                      onChange={(e) => setKpiGoal(e.target.value)}
                      placeholder="e.g., 1000 conversions"
                      className="w-full border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {kpiGoal && (
                      <p className="mt-2 text-sm text-green-600">
                        Estimated budget needed: $
                        {estimatedBudget?.toFixed(2)?.toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date
                    </label>
                    <DatePicker
                      disabledDate={(current) => {
                        return current && current < dayjs().startOf("day");
                      }}
                      value={selectedDate}
                      onChange={handleDateChange}
                      format="MM/DD/YYYY"
                      className="w-full border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <DatePicker
                      disabledDate={(current) => {
                        if (!selectedDate) {
                          return current && current < dayjs().startOf("day");
                        }
                        return current && current < selectedDate.startOf("day");
                      }}
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(e);
                      }}
                      format="MM/DD/YYYY"
                      className="w-full border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 mt-4 items-end">
                <div className="flex justify-start">
                  <Button
                    onClick={() => setShowConstraints(!showConstraints)}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <Settings className="h-4 w-4" />
                    (Optional) select channel constraints
                  </Button>
                </div>
              </div>

              {showConstraints && (
                <div className="mt-4 space-y-3 bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700">
                    Channel Spend Limits
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(channelConstraints).map(
                      ([channel, constraint]: [string, any]) => (
                        <div
                          key={channel}
                          className="p-3 border rounded-lg shadow-sm bg-white space-y-3"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-800">
                              {channel}
                            </span>
                          </div>

                          {/* {constraint.enabled && ( */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col space-y-1">
                              <label className="text-xs text-gray-600">
                                Min
                              </label>
                              <Input
                                type="number"
                                placeholder="Min $"
                                value={constraint.min}
                                onChange={(e) =>
                                  handleMinChange(channel, e.target.value)
                                }
                                className="px-2 py-1 text-sm border rounded"
                              />
                            </div>
                            <div className="flex flex-col space-y-1">
                              <label className="text-xs text-gray-600">
                                Max
                              </label>
                              <Input
                                type="number"
                                placeholder="Max $"
                                value={constraint.max}
                                onChange={(e) =>
                                  handleMaxChange(channel, e.target.value)
                                }
                                className="px-2 py-1 text-sm border rounded"
                              />
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="p-6">
              <Button
                onClick={handleOptimize}
                disabled={isOptimizing || (activeTab === "kpi" && !kpiGoal)}
                className="w-full  bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {isOptimizing ? (
                  <div className="flex items-center gap-2">
                    <RefreshCw className="h-5 w-5 animate-spin" />
                    Optimizing...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Optimize with Adrsta AI
                  </div>
                )}
              </Button>
            </div>
          </div>
        </div>
        <div className="col-span-1">
          {showResults && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <TrendingUp className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">KPI Increment</p>
                      <p className="text-2xl font-bold text-green-600">
                        {improvementPercent}%
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <DollarSign className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {activeTab === "budget" ? "Budget" : "Optimized Budget"}
                      </p>
                      <p className="text-2xl font-bold text-blue-600">
                        $
                        {activeTab === "budget"
                          ? totalOptimized?.toFixed(2)?.toLocaleString()
                          : optimizationResponse &&
                            "results" in optimizationResponse &&
                            "estimated_budget" in optimizationResponse.results
                          ? (
                              optimizationResponse as BudgetOptimizationResponse
                            ).results.total_budget.toLocaleString()
                          : totalOptimized?.toFixed(2)?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Target className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">
                        {kpiType === "conversions"
                          ? "Estimated KPI"
                          : `Est. ${
                              kpiType.charAt(0).toUpperCase() + kpiType.slice(1)
                            }`}
                      </p>
                      <p className="text-2xl font-bold text-purple-600">
                        {Number(totalConversions)?.toFixed()?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Allocation Results
                  </h3>
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={optimizedData.sort(
                      (a, b) => b.optimized - a.optimized
                    )}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="channel" />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => [`$${value.toLocaleString()}`, ""]}
                    />
                    <Legend />
                    <Bar dataKey="Original" fill="#9CA3AF" name="Original" />
                    <Bar dataKey="optimized" fill="#2563EB" name="Optimized" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MMMScenarioPlanner;
